/**
 * Form primitives.
 *
 * Every control is wrapped by `Field`, which owns the label/hint/error wiring.
 * The product this replaces put a red "Password must be 9–60 characters…"
 * paragraph under a field before the user had typed anything, then left the
 * hint in place after it was satisfied. Here a hint is a hint and an error is
 * an error: hints are always visible and neutral, errors appear only after a
 * blur or a submit and are announced.
 */

import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { Icon } from './Icon'
import { cx } from './core'

/* ----------------------------------------------------------------- field -- */

export interface FieldProps {
  label: ReactNode
  /** Marks the control required and appends the required affordance to the label. */
  required?: boolean
  /** Always-visible guidance — constraints, units, maxima. */
  hint?: ReactNode
  /** Shown only when non-empty; replaces nothing, sits under the hint. */
  error?: ReactNode
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
  /** Right-aligned adornment on the label row, e.g. a "Max ₹9,73,656" chip. */
  aside?: ReactNode
  className?: string
}

export function Field({ label, required, hint, error, children, aside, className }: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined

  return (
    <div className={cx('w7-field', className)}>
      <div className="w7-row" style={{ gap: 8 }}>
        <label className="w7-field-label" htmlFor={id}>
          {label}
          {required ? (
            <span className="w7-field-req" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
        {aside ? <span style={{ marginLeft: 'auto' }}>{aside}</span> : null}
      </div>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <p className="w7-field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="w7-field-error" id={errorId} role="alert">
          <Icon name="error" size={13} />
          {error}
        </p>
      ) : null}
    </div>
  )
}

/* ----------------------------------------------------------------- input -- */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  invalid?: boolean
  /** Right-align and tabular-align — for every numeric input. */
  numeric?: boolean
  prefix?: ReactNode
  suffix?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, numeric, prefix, suffix, className, ...rest },
  ref,
) {
  const input = (
    <input
      ref={ref}
      className={cx('w7-input', className)}
      aria-invalid={invalid || undefined}
      data-align={numeric ? 'right' : undefined}
      inputMode={numeric ? 'decimal' : rest.inputMode}
      {...rest}
    />
  )

  if (!prefix && !suffix) return input
  return (
    <span className="w7-affix" data-invalid={invalid || undefined}>
      {prefix ? <span className="w7-affix-prefix">{prefix}</span> : null}
      {input}
      {suffix ? <span className="w7-affix-suffix">{suffix}</span> : null}
    </span>
  )
})

/** A money input — always prefixed, always right-aligned, always decimal. */
export const MoneyInput = forwardRef<HTMLInputElement, InputProps>(function MoneyInput(props, ref) {
  return <Input ref={ref} numeric prefix="₹" placeholder="0.00" {...props} />
})

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
  options: { value: string; label: string; disabled?: boolean }[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, options, placeholder, className, ...rest },
  ref,
) {
  return (
    <span className="w7-selectwrap">
      <select
        ref={ref}
        className={cx('w7-select', className)}
        aria-invalid={invalid || undefined}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <Icon name="chevron-down" size={15} className="w7-select-caret" />
    </span>
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} className={cx('w7-textarea', className)} {...rest} />
  },
)

/* --------------------------------------------------------------- toggles -- */

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      className="w7-toggle"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  )
}

export function Checkbox({
  checked,
  indeterminate,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  indeterminate?: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="checkbox"
      className="w7-check"
      aria-checked={indeterminate ? 'mixed' : checked}
      data-indeterminate={indeterminate ? 'true' : undefined}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <Icon name={indeterminate ? 'minus' : 'check'} size={12} />
    </button>
  )
}

/* ---------------------------------------------------------------- search -- */

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search',
  label,
  width,
}: {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  label: string
  width?: number | string
}) {
  return (
    <span className="w7-affix" style={{ width }}>
      <span className="w7-affix-prefix">
        <Icon name="search" size={15} />
      </span>
      <input
        type="search"
        className="w7-input"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
      />
      {value ? (
        <button
          type="button"
          className="w7-affix-suffix"
          aria-label="Clear search"
          onClick={() => onChange('')}
        >
          <Icon name="close" size={14} />
        </button>
      ) : null}
    </span>
  )
}

/* ------------------------------------------------------------ preset row -- */

/**
 * Stake/amount presets. Present on the give/take drawer because the amounts an
 * operator moves are overwhelmingly round numbers, and typing "50000" into a
 * bare box is both slower and easier to fat-finger by a factor of ten.
 */
export function PresetAmounts({
  presets,
  onPick,
  format,
  disabled,
}: {
  presets: number[]
  onPick: (amount: number) => void
  format: (amount: number) => string
  disabled?: boolean
}) {
  return (
    <div className="w7-row-wrap" style={{ gap: 6 }}>
      {presets.map((preset) => (
        <button
          key={preset}
          type="button"
          className="w7-filterchip"
          onClick={() => onPick(preset)}
          disabled={disabled}
        >
          {format(preset)}
        </button>
      ))}
    </div>
  )
}
